import React, { useEffect, useState } from 'react';
import './ProgressBarBlock.css';

interface ProgressBarData {
  start_time?: string;
  end_time?: string;
  total_duration_seconds?: number;
  label?: string;
  show_percentage?: boolean;
  show_time_remaining?: boolean;
  color?: string;
}

interface ProgressBarBlockData {
  progress_bar?: ProgressBarData;
}

interface ProgressBarBlockProps {
  block: ProgressBarBlockData;
}

const ProgressBarBlock: React.FC<ProgressBarBlockProps> = ({ block }) => {
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  const progressBarData = block.progress_bar || {};
  const {
    start_time,
    end_time,
    total_duration_seconds,
    label = 'Progress',
    show_percentage = true,
    show_time_remaining = true,
    color = '#007bff',
  } = progressBarData;

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Calculate progress percentage
  const calculateProgress = (): number => {
    if (!start_time) return 0;

    const startDate = new Date(start_time);
    const now = currentTime;

    // Calculate end time based on start_time + total_duration_seconds if end_time is null
    let endDate;
    if (end_time) {
      endDate = new Date(end_time);
    } else if (total_duration_seconds) {
      endDate = new Date(startDate.getTime() + total_duration_seconds * 1000);
    } else {
      return 0;
    }

    if (now <= startDate) return 0;
    if (now >= endDate) return 100;

    const totalDuration = endDate.getTime() - startDate.getTime();
    const elapsed = now.getTime() - startDate.getTime();

    return Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100);
  };

  // Calculate time remaining
  const calculateTimeRemaining = (): string | null => {
    if (!start_time) return null;

    const startDate = new Date(start_time);
    const now = currentTime;

    // Calculate end time based on start_time + total_duration_seconds if end_time is null
    let endDate;
    if (end_time) {
      endDate = new Date(end_time);
    } else if (total_duration_seconds) {
      endDate = new Date(startDate.getTime() + total_duration_seconds * 1000);
    } else {
      return null;
    }

    if (now >= endDate) return 'Completed';

    const remaining = endDate.getTime() - now.getTime();
    const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const progress = calculateProgress();
  const timeRemaining = calculateTimeRemaining();

  return (
    <div className="progress-bar-block">
      <div className="progress-bar-header">
        <span className="progress-bar-label">{label}</span>
        <div className="progress-bar-info">
          {show_percentage && (
            <span className="progress-percentage">{Math.round(progress)}%</span>
          )}
          {show_time_remaining && timeRemaining && (
            <span className="progress-time-remaining">{timeRemaining}</span>
          )}
        </div>
      </div>

      <div className="progress-bar-container">
        <div
          className="progress-bar-fill"
          style={{
            width: `${progress}%`,
            backgroundColor: color,
          }}
        />
      </div>
    </div>
  );
};

export default ProgressBarBlock;
